/**
 * UnmooredSkillEffect — Conjuration Tether path (Fracture branch) passive:
 * shade death speed boost.
 *
 * Always-on passive. When any shade dies — combat, Fracture expiry, player
 * dismissal, doesn't matter — the player gains 8% movement speed for
 * 4 seconds. Losing a piece of yourself makes the rest faster.
 *
 * The buff refreshes on subsequent shade deaths but does not stack intensity:
 * three shades dying 200ms apart still yields a single 8% boost whose timer
 * resets to 4000ms on each death. The buff is a single instance, not three.
 *
 * Integration hook:
 *   - `onShadeDestroyed()` — called by the integration layer whenever any
 *     shade is destroyed, regardless of cause. Starts or refreshes the
 *     speed buff timer.
 *   - The integration layer reads `speedBuffActive` and `speedBonus` each
 *     frame to apply the movement speed modifier to the player.
 *
 * UpgradeEffectResolver keys (scoped to tree 'conjuration'):
 * - shadeDeathSpeedBonus: additive, base 0.08
 * - shadeDeathSpeedDurationMs: max, base 4000
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Unmoored
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface UnmooredDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface UnmooredConfig {
  /** Base speed bonus as a fraction (0.08 = 8%). */
  readonly shadeDeathSpeedBonus: number;
  /** Base duration of the speed buff in ms. */
  readonly shadeDeathSpeedDurationMs: number;
}

const DEFAULT_CONFIG: UnmooredConfig = {
  shadeDeathSpeedBonus: 0.08,       // spec § Unmoored: 8%
  shadeDeathSpeedDurationMs: 4000,  // spec § Unmoored: 4 seconds
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class UnmooredSkillEffect implements SkillEffect {
  readonly skillId = 'fracture_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: UnmooredDeps;
  private readonly config: UnmooredConfig;

  /** Time remaining on the current speed buff, in ms. 0 = inactive. */
  private _buffRemainingMs = 0;

  /** Total number of shade death triggers since construction. */
  private _triggerCount = 0;

  constructor(deps: UnmooredDeps, config: Partial<UnmooredConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Unmoored-specific API ---

  /**
   * Resolve the effective speed bonus. Additive stacking: base 0.08
   * plus bonuses from future speed-boost nodes.
   */
  getResolvedSpeedBonus(): number {
    const bonus = this.deps.resolver.getAdditiveEffect('conjuration', 'shadeDeathSpeedBonus');
    return this.config.shadeDeathSpeedBonus + bonus;
  }

  /**
   * Resolve the effective buff duration. Max aggregation: the highest
   * value among base and any resolver overrides wins.
   */
  getResolvedDurationMs(): number {
    const override = this.deps.resolver.getMaxEffect('conjuration', 'shadeDeathSpeedDurationMs');
    return override !== undefined
      ? Math.max(this.config.shadeDeathSpeedDurationMs, override)
      : this.config.shadeDeathSpeedDurationMs;
  }

  /**
   * Called by the integration layer when any shade is destroyed.
   * Starts or refreshes the speed buff timer.
   */
  onShadeDestroyed(): void {
    this._triggerCount++;
    this._buffRemainingMs = this.getResolvedDurationMs();
  }

  // --- State getters ---

  /** Whether the speed buff is currently active. */
  get speedBuffActive(): boolean { return this._buffRemainingMs > 0; }

  /** The current speed bonus (0 if buff is not active). */
  get speedBonus(): number {
    return this.speedBuffActive ? this.getResolvedSpeedBonus() : 0;
  }

  /** Time remaining on the buff in ms. */
  get buffRemainingMs(): number { return this._buffRemainingMs; }

  /** Total number of shade-death triggers. */
  get triggerCount(): number { return this._triggerCount; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return false; // passive — not manually activated
  }

  activate(): boolean {
    return false;
  }

  isActive(): boolean {
    return false;
  }

  get cooldown(): number { return 0; }

  update(delta: number): void {
    if (this._buffRemainingMs > 0) {
      this._buffRemainingMs = Math.max(0, this._buffRemainingMs - delta);
    }
  }

  destroy(): void {
    this._buffRemainingMs = 0;
  }
}
